import dayjs, { Dayjs } from 'dayjs';
import { FieldValues } from 'react-hook-form';

import { RmpcNestedDictionaryType } from '@/api/integrations/rmpc/types/types';

import { RuleOption, Schema, SelectOption } from '../types';
import { ruleHandlers, RuleKey } from '../utils/rules';

export enum RuleValueType {
  TODAY = 'today',
  EIGHTEEN_YEARS_AGO = 'eighteen_years_ago',
  FOURTEEN_YEARS_AGO = 'fourteen_years_ago',
  ONE_YEAR_AGO = 'one_year_ago',
}

export const parseDateRuleValue = (ruleValue: any, watchedValues: FieldValues): Dayjs | undefined => {
  if (ruleValue === null || ruleValue === undefined || ruleValue === '') {
    return undefined;
  }

  const today = dayjs().startOf('day');

  if (ruleValue === RuleValueType.TODAY) {
    return today;
  }

  if (ruleValue === RuleValueType.EIGHTEEN_YEARS_AGO) {
    return today.subtract(18, 'year');
  }

  if (ruleValue === RuleValueType.FOURTEEN_YEARS_AGO) {
    return today.subtract(14, 'year');
  }

  if (ruleValue === RuleValueType.ONE_YEAR_AGO) {
    return today.subtract(1, 'year');
  }

  const specificDate = dayjs(ruleValue, 'YYYY-MM-DD', true);
  if (specificDate.isValid()) {
    return specificDate.startOf('day');
  }

  if (typeof ruleValue === 'string' && watchedValues && watchedValues[ruleValue] !== undefined) {
    const fieldValue = watchedValues[ruleValue];

    const fieldDate = dayjs(fieldValue);
    if (fieldDate.isValid()) {
      return fieldDate.startOf('day');
    }
  }

  return undefined;
};

export const getMinDateFromFieldRule = (
  schema: Schema,
  name: string,
  watchedValues: FieldValues,
): { date: Dayjs | undefined; isTodayRule: boolean } => {
  const rules: RuleOption[] | undefined = schema?.$rules?.[name];
  if (!Array.isArray(rules)) {
    return { date: undefined, isTodayRule: false };
  }

  let minDate: Dayjs | undefined = undefined;
  let isTodayRulePresent = false;

  rules.forEach((rule) => {
    if (rule.key === RuleKey.AFTER || rule.key === RuleKey.AFTER_OR_EQUAL) {
      if (rule.value === RuleValueType.TODAY) {
        isTodayRulePresent = true;
      }

      const candidateDate = parseDateRuleValue(rule.value, watchedValues);
      const isStrictlyAfter = rule.key === RuleKey.AFTER;

      if (candidateDate) {
        const effectiveDate = isStrictlyAfter ? candidateDate.add(1, 'day') : candidateDate;

        if (!minDate || effectiveDate.isAfter(minDate)) {
          minDate = effectiveDate;
        }
      }
    }
  });

  return { date: minDate, isTodayRule: isTodayRulePresent };
};

export const getMaxDateFromFieldRule = (
  schema: Schema,
  name: string,
  watchedValues: FieldValues,
): { date: Dayjs | undefined; isTodayRule: boolean } => {
  const rules: RuleOption[] | undefined = schema?.$rules?.[name];
  if (!Array.isArray(rules)) {
    return { date: undefined, isTodayRule: false };
  }

  let maxDate: Dayjs | undefined = undefined;
  let isTodayRulePresent = false;

  rules.forEach((rule) => {
    if (rule.key === RuleKey.BEFORE || rule.key === RuleKey.BEFORE_OR_EQUAL) {
      if (rule.value === RuleValueType.TODAY) {
        isTodayRulePresent = true;
      }

      const candidateDate = parseDateRuleValue(rule.value, watchedValues);
      const isStrictlyBefore = rule.key === RuleKey.BEFORE;

      if (candidateDate) {
        const effectiveDate = isStrictlyBefore ? candidateDate.subtract(1, 'day') : candidateDate;

        if (!maxDate || effectiveDate.isBefore(maxDate)) {
          maxDate = effectiveDate;
        }
      }
    }
  });

  return { date: maxDate, isTodayRule: isTodayRulePresent };
};

export const getRuleErrorMessage = (schema: Schema, fieldName: string, ruleKey: RuleKey): string | undefined => {
  const rules = schema.$rules?.[fieldName];
  const rule = rules?.find((rule) => rule.key === ruleKey);
  return rule?.errorMessage;
};

export function checkRule(
  ruleKey: RuleKey,
  schema: Schema,
  fieldName: string,
  watchedValues: Record<string, any>,
): boolean {
  const rules = schema.$rules?.[fieldName];
  const rule = rules?.find((r) => r.key === ruleKey);
  const handler = ruleHandlers[ruleKey];

  return handler(rule?.value, schema, fieldName, watchedValues);
}

export function getSelectOptionsFromInRule(schema: any, name: string): SelectOption[] | undefined {
  const dict = schema.$dictionaries?.[name];
  if (Array.isArray(dict)) {
    return dict.map(
      ({ key, value }): SelectOption => ({
        id: key != null ? String(key) : null,
        label: value,
      }),
    );
  }
  return undefined;
}

export function getOptionsForField(
  schema: Schema,
  fieldName: string,
  watchedValues: Record<string, any>,
  externalDictionaries?: Record<string, RmpcNestedDictionaryType[]>,
): SelectOption[] {
  const fieldSchema = schema.properties?.[fieldName];
  if (!fieldSchema) return [];

  let options: SelectOption[] = getSelectOptionsFromInRule(schema, fieldName) || [];

  if (options.length === 0) {
    if (fieldSchema.parentField && externalDictionaries) {
      const parentValue = watchedValues[fieldSchema.parentField];
      const parentSchema = schema.properties?.[fieldSchema.parentField];
      const parentDictionaryKey = parentSchema?.dictionaryKey;

      if (parentDictionaryKey && parentValue && externalDictionaries[parentDictionaryKey]) {
        const parentItem = externalDictionaries[parentDictionaryKey].find(
          (item) => String(item.id) === String(parentValue),
        );
        if (parentItem?.items) {
          options = parentItem.items.map(
            (item): SelectOption => ({
              id: String(item.id),
              label: item.name,
            }),
          );
        }
      }
    } else if (fieldSchema.dictionaryKey && externalDictionaries) {
      options =
        externalDictionaries[fieldSchema.dictionaryKey]?.map(
          (item): SelectOption => ({
            id: String(item.id),
            label: item.name,
          }),
        ) || [];
    }
  }

  if (options.length === 0) {
    options =
      schema.$dictionaries?.[fieldName]?.map(
        ({ key, value }): SelectOption => ({
          id: key != null ? String(key) : null,
          label: value,
        }),
      ) || [];
  }

  return options;
}

export function isFieldDisabled(schema: Schema, fieldName: string, watchedValues: Record<string, any>): boolean {
  const fieldSchema = schema.properties?.[fieldName];
  if (!fieldSchema) return false;

  if (fieldSchema.parentField) {
    const parentValue = watchedValues[fieldSchema.parentField];
    return !parentValue;
  }

  return false;
}
